import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import {
  correctClockEntry,
  createManualClockEntry,
  fetchClockStatus,
} from "../../../api/TimeTracking/timeTrackingApi";
import {
  CLOCK_EVENT_OPTIONS,
  PAUSE_TYPE_OPTIONS,
} from "./clockEventLabels";

const WORK_MODES = [
  { value: "presencial", label: "Presencial" },
  { value: "teletrabajo", label: "Teletrabajo" },
  { value: "hibrido", label: "Híbrido" },
];

function toDatetimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function recordedAtFromLocal(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const ClockAdjustDialog = ({
  open,
  mode,
  entry,
  defaultUserId,
  defaultRecordedAt,
  employees = [],
  onClose,
  onSaved,
}) => {
  const isCorrect = mode === "correct";
  const [userId, setUserId] = useState("");
  const [eventType, setEventType] = useState("clock_in");
  const [pauseType, setPauseType] = useState("comida");
  const [workMode, setWorkMode] = useState("presencial");
  const [recordedAt, setRecordedAt] = useState("");
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [clockStatus, setClockStatus] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason("");
    if (isCorrect && entry) {
      setUserId(String(entry.userId));
      setEventType(entry.eventType);
      setPauseType(entry.pauseType || "comida");
      setWorkMode(entry.workMode || "presencial");
      setRecordedAt(toDatetimeLocalValue(entry.recordedAt));
    } else {
      setUserId(defaultUserId ? String(defaultUserId) : "");
      setEventType("clock_in");
      setPauseType("comida");
      setWorkMode("presencial");
      setRecordedAt(
        toDatetimeLocalValue(defaultRecordedAt || new Date().toISOString()),
      );
    }
  }, [open, isCorrect, entry, defaultUserId, defaultRecordedAt]);

  useEffect(() => {
    if (!open || isCorrect || !userId) {
      setClockStatus(null);
      return;
    }
    let cancelled = false;
    fetchClockStatus({ userId: Number(userId) })
      .then((status) => {
        if (!cancelled) {
          setClockStatus(status);
          if (status?.isClockedIn) {
            setEventType("clock_out");
          }
        }
      })
      .catch(() => {
        if (!cancelled) setClockStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, isCorrect, userId]);

  const openSessionBlocksClockIn =
    !isCorrect && clockStatus?.isClockedIn && eventType === "clock_in";

  const handleSave = async () => {
    const iso = recordedAtFromLocal(recordedAt);
    if (!iso) {
      setError("Indica fecha y hora válidas");
      return;
    }
    if (!reason.trim()) {
      setError("Indica el motivo (obligatorio para trazabilidad)");
      return;
    }
    if (!isCorrect && !userId) {
      setError("Selecciona el empleado");
      return;
    }
    if (eventType === "pause_start" && !pauseType) {
      setError("Indica el tipo de pausa");
      return;
    }

    if (openSessionBlocksClockIn) {
      setError(
        "Este empleado tiene una jornada abierta. Añade la salida olvidada o usa Corregir en un fichaje existente.",
      );
      return;
    }

    const notes = eventType === "pause_start" ? pauseType : undefined;

    try {
      setSaving(true);
      setError(null);
      if (isCorrect) {
        await correctClockEntry(entry.id, {
          eventType,
          workMode,
          recordedAt: iso,
          reason: reason.trim(),
          notes,
        });
      } else {
        await createManualClockEntry({
          userId: Number(userId),
          eventType,
          workMode,
          recordedAt: iso,
          reason: reason.trim(),
          notes,
        });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.error || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isCorrect ? "Corregir fichaje" : "Añadir fichaje olvidado"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {isCorrect ? (
            <Alert severity="info" variant="outlined">
              Se creará una fila nueva con la hora corregida. El registro
              original se conserva en el historial (trazabilidad legal).
            </Alert>
          ) : (
            <Alert severity="info" variant="outlined">
              Para cuando el empleado olvidó fichar entrada o salida. Quedará
              registrado quién lo añadió y el motivo.
            </Alert>
          )}

          {!isCorrect && clockStatus?.isClockedIn ? (
            <Alert severity="warning" variant="outlined">
              Jornada abierta desde{" "}
              {clockStatus.lastEntry?.recordedAt
                ? new Date(clockStatus.lastEntry.recordedAt).toLocaleString(
                    "es-ES",
                    { dateStyle: "short", timeStyle: "short" },
                  )
                : "—"}
              . Solo puedes añadir salida (o pausa) hasta cerrarla.
            </Alert>
          ) : null}

          {!isCorrect ? (
            <FormControl size="small" fullWidth>
              <InputLabel>Empleado</InputLabel>
              <Select
                value={userId}
                label="Empleado"
                onChange={(e) => setUserId(e.target.value)}
              >
                {employees.map((e) => (
                  <MenuItem key={e.id} value={String(e.id)}>
                    {e.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <FormControl size="small" fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={eventType}
              label="Tipo"
              onChange={(e) => setEventType(e.target.value)}
            >
              {CLOCK_EVENT_OPTIONS.map((opt) => {
                const blocked =
                  !isCorrect &&
                  clockStatus?.isClockedIn &&
                  opt.value === "clock_in";
                return (
                  <MenuItem key={opt.value} value={opt.value} disabled={blocked}>
                    {opt.label}
                    {blocked ? " (jornada abierta)" : ""}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {eventType === "pause_start" ? (
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo de pausa</InputLabel>
              <Select
                value={pauseType}
                label="Tipo de pausa"
                onChange={(e) => setPauseType(e.target.value)}
              >
                {PAUSE_TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}

          <FormControl size="small" fullWidth>
            <InputLabel>Modalidad</InputLabel>
            <Select
              value={workMode}
              label="Modalidad"
              onChange={(e) => setWorkMode(e.target.value)}
            >
              {WORK_MODES.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Fecha y hora"
            type="datetime-local"
            size="small"
            fullWidth
            value={recordedAt}
            onChange={(e) => setRecordedAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Motivo"
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ej.: olvidó fichar al llegar, error en la hora de salida…"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Guardar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClockAdjustDialog;
